import React, { useEffect, useState } from 'react';
import { Snackbar, Box, Alert, Typography, Button, MenuItem, Select, InputLabel, CircularProgress, FormControl, Grid2, TextField, Autocomplete } from '@mui/material';
import { jobServices } from '../../services/jobServices';
import { reportServices } from '../../services/reportServices';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../services/authentication/authContext';
import Accordion from '@mui/material/Accordion';
import '../../css/JobTrack.css';
import '../../css/Button.css';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GetApp, Clear, Save, Schedule, Beenhere, People, BusinessCenter, EventNote, Timer } from '@mui/icons-material';
import PageHeader from '../ui/PageHeader';
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import { downloadReport } from '../../utils/downloadReport ';
import ActionProgressDialog from '../common/ActionProgressDialog';

const JobTrack = () => {

  const [selectedJobId, setSelectedJobId] = useState('');
  const [jobData, setJobData] = useState([]);
  const [shortListedJobs, setShortListedJobs] = useState([]);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobType, setJobType] = useState([]);
  const [selectedJobType, setSelectedJobType] = useState('');
  const [jobFullData, setJobFullData] = useState(null);
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuth();
  const [clientList, setClientList] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [jobInput, setJobInput] = useState('');
  const [internalJobInput, setInternalJobInput] = useState('');
  const [roles, setRoles] = useState([]);
  const isAdmin = roles.includes('ADMIN') || roles.includes('SUPER_ADMIN');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [jobPlanJson, setJobPlanJson] = useState([]);
  const [checkListJson, setCheckListJson] = useState([]);

  useEffect(() => {
    if (!accessToken) return;

    const fetchClients = async () => {
      try {
        const decodedToken = jwtDecode(accessToken);
        let extractedRoles = [];

        if (Array.isArray(decodedToken.roles)) {
          extractedRoles = decodedToken.roles;
        } else if (typeof decodedToken.roles === "string") {
          extractedRoles = decodedToken.roles.split(",").map(role => role.trim());
        }

        setRoles(extractedRoles);
        setLoading(true);

        const response = await jobServices.getClientsForJobTrackScreen();
        setClientList(response);

      } catch (error) {
        setError(error?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [accessToken]);

  const handleClear = () => {
    setSelectedClientId('');
    setSelectedJobType('');
    setSelectedJobId('');
    setShortListedJobs([]);
    setJobType([]);
    setJobData([]);
    setJobFullData(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const handleReport = async () => {
    setIsDownloading(true);
    setDownloadMessage('Generating Job Track Report...');
    try {
      if (selectedClientId && jobData) {
        const response = await reportServices.getJobReport(selectedJobId);

        downloadReport(response);

        setSnackbarMessage("Report Generated Successfully!");
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        setTimeout(() => {
          setSnackbarMessage(null);
          setOpenSnackbar(false);
        }, 5000);

        handleClear();
      }
    } catch (error) {
      setError(error.message);
      setTimeout(() => setError(null), 4000);

    } finally {
      setIsDownloading(false);
    }
  };

  const handleJobChange = (e) => {
    const jobId = e.target.value;
    setSelectedJobId(jobId);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleClientChange = async (e) => {
    const clientId = e.target.value;
    setSelectedClientId(clientId);

    setLoading(true);

    try {
      const response = await jobServices.getJobsByClientId(clientId);
      setJobData(response);
      setJobType([...new Set(response.map(j => j.type))]);
      setSelectedJobType('');
      setShortListedJobs([]);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setJobData([]);
        setJobType([]);
      } else {
        setError(error.message);
        setJobData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = e => {
    const selectedType = e.target.value;
    setSelectedJobType(selectedType);
    const filteredJobs = jobData.filter(j => j.type === selectedType);
    setShortListedJobs(filteredJobs);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    setLoading(true);
    jobServices.getJobFullDetailsById(selectedJobId)
      .then((response) => {
        setJobFullData(response);
        setJobPlanJson(response.budgetHoursJson);
        setCheckListJson(response.checklistJson);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });

  };

  const jobFilter = (options, { inputValue }) => {
    return options.filter((option) =>
      option?.name?.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const handeJobPlanSubmit = (event) => {
    event.preventDefault();
    setIsDownloading(true);
    setDownloadMessage('Generating Job Plan Report...');
    reportServices.downloadJobPlanReport(selectedJobId)
      .then((response) => {
        downloadReport(response);
        setIsDownloading(false);
      })
      .catch((error) => {
        setIsDownloading(false);
      });
  }

  const handeJobChecklistSubmit = (event) => {
    event.preventDefault();
    setIsDownloading(true);
    setDownloadMessage('Generating Job Checklist...');
    reportServices.downloadJobChecklistReport(selectedJobId)
      .then((response) => {
        downloadReport(response);
        setIsDownloading(false);
      })
      .catch((error) => {
        setIsDownloading(false);
      });
  }

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '';

    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      dateStr = dateStr.trim();

      // Expected: "DD-MM-YYYY hh:mm:ss am" OR "DD-MM-YYYY hh:mm:ss"
      const parts = dateStr.split(/\s+/);
      let datePart, timePart, period;
      if (parts.length === 3) {
        [datePart, timePart, period] = parts;
        period = period.toUpperCase();
      } else if (parts.length === 2) {
        [datePart, timePart] = parts;
        period = null;
      } else {
        return null;
      }

      const [day, month, year] = datePart.split('-').map(p => p.trim());
      if (!day || !month || !year) return null;

      const t = timePart.split(':').map(p => p.trim());
      const rawHours = parseInt(t[0] || '0', 10);
      const minutes = parseInt(t[1] || '0', 10);
      const seconds = parseInt(t[2] || '0', 10);

      if (isNaN(rawHours) || isNaN(minutes) || isNaN(seconds)) return null;

      let hours = rawHours;
      if (period) {
        if (period === 'PM' && hours < 12) hours += 12;
        else if (period === 'AM' && hours === 12) hours = 0;
      }

      // Use numeric Date constructor to avoid locale/ISO parsing issues
      return new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        hours,
        minutes,
        seconds
      );
    };

    const start = parseDate(startTime);
    const end = parseDate(endTime);

    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      return "Invalid date";
    }

    const durationInMillis = end.getTime() - start.getTime();
    if (durationInMillis < 0) {
      return "End time cannot be earlier than start time";
    }

    const durationInSeconds = Math.floor(durationInMillis / 1000);
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = durationInSeconds % 60;

    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const getStatusClass = (completed) => {
    return completed ? 'status-completed' : 'status-in-progress';
  };

  const renderTaskHistory = (taskHistory) => {
    return (
      <div style={{ overflowX: 'auto' }}>
        <table className="modern-table">
          <thead>
            <tr>
              <th className="modern-table-header task-col">Task Title</th>
              <th className="modern-table-header user-col">Assigned User</th>
              <th className="modern-table-header time-range-col">Time Range</th>
              <th className="modern-table-header duration-col">Duration</th>
              <th className="modern-table-header">Status</th>
            </tr>
          </thead>
          <tbody>
            {taskHistory && taskHistory.length > 0 ? (
              taskHistory.map((task, index) => (
                <tr key={index} className="modern-table-row">
                  <td className="modern-table-cell task-col">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{task.titles}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>{task.description}</Typography>
                  </td>
                  <td className="modern-table-cell user-col">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <People sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {task.assignedUser ? `${task.assignedUser.firstName} ${task.assignedUser.lastName}` : 'N/A'}
                      </Typography>
                    </Box>
                  </td>
                  <td className="modern-table-cell time-range-col">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography variant="caption" sx={{ color: '#475569', display: 'flex', alignItems: 'center' }}>
                        <span className="time-label">S</span> {task.startTime}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#475569', display: 'flex', alignItems: 'center' }}>
                        <span className="time-label">E</span> {task.endTime || '---'}
                      </Typography>
                    </Box>
                  </td>
                  <td className="modern-table-cell duration-col cell-nowrap">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#334155' }}>
                      <Timer sx={{ fontSize: 18, color: '#64748b' }} />
                      <Typography variant="body2" sx={{ fontWeight: 800, letterSpacing: '0.2px' }}>
                        {calculateDuration(task.startTime, task.endTime)}
                      </Typography>
                    </Box>
                  </td>
                  <td className="modern-table-cell">
                    <span className={`status-badge ${getStatusClass(task.taskCompleted)}`}>
                      {task.taskCompleted ? 'Completed' : 'Active'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="modern-table-cell" style={{ textAlign: 'center', padding: '1.875rem' }}>
                  No tasks available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="jobtrack-container">
      <PageHeader title="Job Track" />

      <Snackbar open={openSnackbar} autoHideDuration={5000} onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box className="jobtrack-box" sx={{ mb: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid2 container spacing={3}>
            {/* Client Name (Autocomplete with client ID) */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={clientList || []}
                getOptionLabel={(option) => option?.name || ''}
                value={clientList.find(client => client.id === selectedClientId) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleClientChange({ target: { value: newValue.id } });
                  } else {
                    setSelectedClientId('');
                    setJobData([]);
                    setJobType([]);
                    setSelectedJobType('');
                    setShortListedJobs([]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                noOptionsText={t('No options')}
                disableClearable
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={jobType}
                getOptionLabel={(option) => option || ''}
                value={selectedJobType || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleTypeChange({ target: { value: newValue } });
                  } else {
                    setSelectedJobType('');
                    setShortListedJobs([]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Job Type"
                    fullWidth
                  />
                )}
                loading={loading}
                isOptionEqualToValue={(option, value) => option === value}
                noOptionsText={t('No options')}
                disableClearable
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={shortListedJobs}
                getOptionLabel={(option) => option?.name || ''}
                value={shortListedJobs.find(job => job.id === selectedJobId) || null}
                inputValue={jobInput}
                loading={jobLoading}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setJobInput(newInputValue);
                    setInternalJobInput(newInputValue + '  ');
                    setTimeout(() => {
                      setJobInput(newInputValue);
                      setInternalJobInput(newInputValue);
                    }, 10);
                  } else {
                    setJobInput(newInputValue);
                    setInternalJobInput(newInputValue);
                  }
                }}
                filterOptions={(options, state) => {
                  return jobFilter(options, { inputValue: internalJobInput });
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleJobChange({ target: { value: newValue.id } });
                    setJobInput(newValue.name);
                    setInternalJobInput(newValue.name);
                  } else {
                    setSelectedJobId('');
                    setJobInput('');
                    setInternalJobInput('');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Job Name"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                noOptionsText={t('No options')}
                disableClearable
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }} sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              <Button type="submit" variant="contained" className="commonButton saveButton" disabled={!selectedJobId} sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                <Save className="buttonIcon" /> Show Result
              </Button>
              <Button type="button" variant="outlined" onClick={handleClear} className="commonButton clearButton" sx={{ bgcolor: 'white', flex: { xs: '1 1 auto', sm: '0 1 auto' } }}>
                <Clear className="buttonIcon" /> Clear
              </Button>
              {isAdmin && (
                <Button type="button" variant="contained" onClick={handleReport} className="commonButton reportButton" disabled={!selectedJobId} sx={{ flex: { xs: '1 1 auto', sm: '0 1 auto' } }}>
                  <GetApp className="buttonIcon" /> Report
                </Button>
              )}
            </Grid2>
          </Grid2>
        </form>
      </Box>

      <br />
      <br />

      {jobFullData && (
        <Box sx={{ mt: 2, width: '100%' }}>
          {/* Job Overview Section */}
          <Box className="jobtrack-box" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BusinessCenter color="primary" /> {jobFullData.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Client:</strong> {jobFullData.client.name} ({jobFullData.client.code})
                </Typography>
              </Box>
              <span className={`status-badge ${jobFullData.status === 'Completed' ? 'status-completed' : 'status-in-progress'}`}>
                {jobFullData.status}
              </span>
            </Box>

            <div className="metric-grid">
              <div className="metric-card">
                <Box className="metric-icon-box" sx={{ bgcolor: '#eff6ff' }}>
                  <BusinessCenter sx={{ color: '#2563eb' }} />
                </Box>
                <div className="metric-label">Job Type</div>
                <div className="metric-value">{jobFullData.type}</div>
              </div>
              <div className="metric-card" style={{ borderLeft: '0.25rem solid #3b82f6' }}>
                <Box className="metric-icon-box" sx={{ bgcolor: '#eff6ff' }}>
                  <Schedule sx={{ color: '#2563eb' }} />
                </Box>
                <div className="metric-label">Budget Hours</div>
                <div className="metric-value">{jobFullData.budgetHours}</div>
              </div>
              <div className="metric-card" style={{ borderLeft: '0.25rem solid #10b981' }}>
                <Box className="metric-icon-box" sx={{ bgcolor: '#ecfdf5' }}>
                  <Timer sx={{ color: '#059669' }} />
                </Box>
                <div className="metric-label">Spent Time</div>
                <div className="metric-value">{jobFullData.totalSpentTime}</div>
              </div>
              <div className="metric-card" style={{ borderLeft: '0.25rem solid #f59e0b' }}>
                <Box className="metric-icon-box" sx={{ bgcolor: '#fff7ed' }}>
                  <EventNote sx={{ color: '#d97706' }} />
                </Box>
                <div className="metric-label">Expected Date</div>
                <div className="metric-value">{jobFullData.expectedEndDate ? formatDate(jobFullData.expectedEndDate) : '—'}</div>
              </div>
            </div>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, pt: 3, borderTop: '1px solid #f1f5f9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>
                  {jobFullData.assignedUser.firstName.charAt(0)}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>JOB OWNER</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{jobFullData.assignedUser.firstName + " " + jobFullData.assignedUser.lastName}</Typography>
                </Box>
              </Box>
              {jobPlanJson && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handeJobPlanSubmit}
                  startIcon={<GetApp />}
                  className="commonButton"
                  sx={{ borderRadius: '0.5rem', textTransform: 'none', fontWeight: 700, width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}
                >
                  Job Plan
                </Button>
              )}
            </Box>
          </Box>

          {/* Job Status and Task History */}
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Beenhere color="primary" /> Progress Tracking
          </Typography>

          {jobFullData.statusWithTask &&
            Object.keys(jobFullData.statusWithTask).map((status, index) => (
              <Accordion key={index} className="accordion" sx={{ borderRadius: '1rem !important', mb: 2, overflow: 'hidden' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8fafc', px: 3, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                    <Typography sx={{ fontWeight: 800, color: '#1e293b' }}>{status}</Typography>
                    <Typography variant="caption" sx={{ bgcolor: 'white', px: 1.5, py: 0.5, borderRadius: '0.375rem', border: '1px solid #e2e8f0', fontWeight: 700 }}>
                      {format(new Date(jobFullData.statusWithTask[status].jobStatusDate), 'dd MMM yyyy')}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Status Assigned to: <strong>{jobFullData.statusWithTask[status].assignedUserName}</strong>
                    </Typography>
                  </Box>
                  {renderTaskHistory(jobFullData.statusWithTask[status].taskHistory)}
                </AccordionDetails>
              </Accordion>
            ))}

          {checkListJson && (jobFullData.type === "Bookkeeping" || jobFullData.type === "VAT" || jobFullData.type === "Statutory/Final Accounts") && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 5 }}>
              <Button
                variant="contained"
                onClick={handeJobChecklistSubmit}
                startIcon={<GetApp />}
                className="commonButton reportButton"
                sx={{ px: 4, py: 1.5, borderRadius: '0.75rem', width: { xs: '100%', sm: 'auto' } }}
              >
                Download Job CheckList Report
              </Button>
            </Box>
          )}
        </Box>
      )}

      <ActionProgressDialog open={isDownloading} message={downloadMessage} />
    </div>
  );
}

export default JobTrack;